// ---- Cut Vinyl Lettering Designer ----

import { getClipartCategories, loadSvgContent, formatCategoryName } from './clipart.js';

// ---------- Sidebar tabs (Text / Effects / Sheet) ----------
// Pure DOM behavior, independent of Fabric/canvas state — safe to wire up
// immediately regardless of font-loading or canvas init order.
(function initSidebarTabs() {
  const tabButtons = document.querySelectorAll(".tab-btn");
  const tabPanels = document.querySelectorAll(".tab-panel");
  tabButtons.forEach((btn) => {
    btn.addEventListener("click", () => {
      const target = btn.dataset.tab;
      tabButtons.forEach((b) => b.classList.toggle("active", b === btn));
      tabPanels.forEach((p) => p.classList.toggle("active", p.dataset.tabPanel === target));
    });
  });
})();
// Fixed design resolution: how many px represent one real inch in the backing store.
const PPI = 50;
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 4;
const SHEET_HEIGHT_MAX = 48;
const SHEET_WIDTH_MAX = 96;

// Self-hosted TTFs living in ./fonts, bundled with the app — this is the
// fallback/default list. Each is pinned to ONE exact weight (matching
// fonts/fonts.css) so canvas font-matching is deterministic. These are
// explicitly preloaded via the FontFace API below — waiting on a webfont
// CDN's lazy/async load was the cause of the corner box staying sized for
// the previous font (canvas would measure a fallback font before the real
// one finished loading).
//
// When embedded in WooCommerce, this list is REPLACED at startup by
// fetchManagedFontList() with whatever an admin has configured under
// WooCommerce → Vinyl Designer → Fonts (uploads + built-in fonts they've
// hidden) — see VLD_Fonts on the PHP side. Standalone testing (opened
// directly, no ?ajaxUrl in the URL) always uses this bundled default list.
const DEFAULT_FONT_FILES = [
  { family: "Anton", file: "fonts/Anton-Regular.ttf", weight: "400" },
  { family: "Archivo Black", file: "fonts/ArchivoBlack-Regular.ttf", weight: "400" },
  { family: "Bebas Neue", file: "fonts/BebasNeue-Regular.ttf", weight: "400" },
  { family: "Dancing Script", file: "fonts/DancingScript-Variable.ttf", weight: "700" },
  { family: "Great Vibes", file: "fonts/GreatVibes-Regular.ttf", weight: "400" },
  { family: "Lilita One", file: "fonts/LilitaOne-Regular.ttf", weight: "400" },
  { family: "New Rocker", file: "fonts/NewRocker-Regular.ttf", weight: "400" },
  { family: "Oi", file: "fonts/Oi-Regular.ttf", weight: "400" },
  { family: "Oswald", file: "fonts/Oswald-Variable.ttf", weight: "700" },
  { family: "Pacifico", file: "fonts/Pacifico-Regular.ttf", weight: "400" },
  { family: "Poppins", file: "fonts/Poppins-Bold.ttf", weight: "700" },
  { family: "Russo One", file: "fonts/RussoOne-Regular.ttf", weight: "400" },
  { family: "Teko", file: "fonts/Teko-Variable.ttf", weight: "700" },
  { family: "Zen Dots", file: "fonts/ZenDots-Regular.ttf", weight: "400" },
  { family: "Arial", file: null, weight: "normal", label: "Arial (system)" },
  { family: "Georgia", file: null, weight: "normal", label: "Georgia (system)" },
  { family: "Impact", file: null, weight: "normal", label: "Impact (system)" },
];

let FONT_FILES = DEFAULT_FONT_FILES;
let FONT_WEIGHTS = {};
function rebuildFontWeights() {
  FONT_WEIGHTS = {};
  FONT_FILES.forEach((f) => (FONT_WEIGHTS[f.family] = f.weight));
}
rebuildFontWeights();
function fontWeightFor(family) {
  return FONT_WEIGHTS[family] || "normal";
}

// Builds the <option> list from whatever FONT_FILES currently is — called
// once with the bundled defaults on load, then again if/once the
// admin-managed list is fetched successfully.
function populateFontSelect() {
  const select = els.selectFont;
  const previousValue = select.value;
  select.innerHTML = "";
  FONT_FILES.forEach((f) => {
    const opt = document.createElement("option");
    opt.value = f.family;
    opt.textContent = f.label || f.family;
    if (f.file) {
      opt.style.fontFamily = `'${f.family}'`;
      if (f.weight && f.weight !== "normal") opt.style.fontWeight = f.weight;
    }
    select.appendChild(opt);
  });
  // Keep whatever was selected if it still exists in the new list (e.g. the
  // managed list finishes loading after the seed letter already picked a
  // font) — otherwise just leave the browser's default (first option).
  if (previousValue && FONT_FILES.some((f) => f.family === previousValue)) {
    select.value = previousValue;
  }
}

// Bundled fonts already load two ways: a static <link> to fonts/fonts.css
// (real @font-face rules) AND the FontFace API preload below. Admin-
// uploaded custom fonts only ever had the second path — and that's the
// actual cause of "shows in the dropdown but renders as a generic serif
// (Times)": the FontFace API's own registration can fail silently for a
// URL it didn't already know about at parse time, in ways that a plain
// CSS @font-face rule tends not to. This gives every font with a file
// (built-in AND custom alike) a real @font-face declaration too, so
// there's always a second, independent way for the browser to actually
// find and use it — cheap and harmless to duplicate for the built-ins.
function injectFontFaceStyles() {
  let styleEl = document.getElementById("vldDynamicFontFaces");
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = "vldDynamicFontFaces";
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = FONT_FILES.filter((f) => f.file)
    .map((f) => {
      const weight = f.weight && f.weight !== "normal" ? f.weight : "400";
      const safeFamily = f.family.replace(/"/g, '\\"');
      return `@font-face { font-family: "${safeFamily}"; src: url("${f.file}"); font-weight: ${weight}; font-display: swap; }`;
    })
    .join("\n");
}

// Receives the live, admin-managed font list from the WordPress parent
// page (WooCommerce → Vinyl Designer → Fonts) via postMessage — the same
// reliable iframe<->parent channel already used (in the other direction)
// by embed-bridge.js for live price/design sync. This deliberately does
// NOT do its own fetch() out to admin-ajax.php: that has more ways to
// silently fail (URL encoding, CORS, subdirectory installs, caching) than
// a same-page postMessage handshake does.
//
// Standalone testing (no parent to talk to, window.self === window.top)
// or no response within the timeout just keeps the bundled
// DEFAULT_FONT_FILES, so this can never leave the app with no fonts at all.
function fetchManagedFontList() {
  if (window.self === window.top) {
    console.log("[VLD] standalone (not embedded) — using bundled default fonts.");
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let settled = false;

    function onMessage(event) {
      const data = event.data;
      if (!data || data.type !== "vinyl-font-list" || settled) return;
      console.log("[VLD] received font list from parent page:", data.fonts);
      settled = true;
      window.removeEventListener("message", onMessage);
      resolve(Array.isArray(data.fonts) ? data.fonts : null);
    }
    window.addEventListener("message", onMessage);

    // Ask the parent directly too, in case its own iframe "load" listener
    // already fired before this script attached its message listener. The
    // parent (see class-vld-product-template.php) also broadcasts this
    // repeatedly on its own for several seconds regardless, as a second
    // safety net against exact timing.
    try {
      window.parent.postMessage({ type: "vinyl-font-list-request" }, "*");
    } catch (e) {
      // ignore — falls through to the timeout below
    }

    setTimeout(() => {
      if (settled) return;
      console.warn("[VLD] no font list received from parent within 4s — using bundled default fonts.");
      settled = true;
      window.removeEventListener("message", onMessage);
      resolve(null);
    }, 4000);
  }).then((list) => {
    if (!Array.isArray(list) || list.length === 0) return null;
    return list.map((f) => ({
      family: f.family,
      file: f.url || null, // absolute URL from the server, or null for a system font
      weight: f.weight || "normal",
      label: f.label || f.family,
    }));
  });
}

function clamp(v, min, max) {
  return Math.min(max, Math.max(min, v));
}

const els = {
  inputText: document.getElementById("inputText"),
  selectFont: document.getElementById("selectFont"),
  inputItalic: document.getElementById("inputItalic"),
  inputColor: document.getElementById("inputColor"),
  inputHeight: document.getElementById("inputHeight"),
  heightVal: document.getElementById("heightVal"),
  inputSpacing: document.getElementById("inputSpacing"),
  inputStrokeEnabled: document.getElementById("inputStrokeEnabled"),
  inputStrokeColor: document.getElementById("inputStrokeColor"),
  inputStrokeWidth: document.getElementById("inputStrokeWidth"),
  inputGradientEnabled: document.getElementById("inputGradientEnabled"),
  inputGradientColor1: document.getElementById("inputGradientColor1"),
  inputGradientColor2: document.getElementById("inputGradientColor2"),
  selectGradientDirection: document.getElementById("selectGradientDirection"),
  inputSheetHeight: document.getElementById("inputSheetHeight"),
  inputSheetWidth: document.getElementById("inputSheetWidth"),
  inputFlip: document.getElementById("inputFlip"),
  inputSheetColor: document.getElementById("inputSheetColor"),
  inputIncludeSheet: document.getElementById("inputIncludeSheet"),
  btnAddText: document.getElementById("btnAddText"),
  btnBringFront: document.getElementById("btnBringFront"),
  btnSendBack: document.getElementById("btnSendBack"),
  btnBringForward: document.getElementById("btnBringForward"),
  btnSendBackward: document.getElementById("btnSendBackward"),
  btnDelete: document.getElementById("btnDelete"),
  btnCopy: document.getElementById("btnCopy"),
  btnPaste: document.getElementById("btnPaste"),
  btnClear: document.getElementById("btnClear"),
  btnCenterAll: document.getElementById("btnCenterAll"),
  canvasHint: document.getElementById("canvasHint"),
  btnUndo: document.getElementById("btnUndo"),
  btnRedo: document.getElementById("btnRedo"),
  btnZoomIn: document.getElementById("btnZoomIn"),
  btnZoomOut: document.getElementById("btnZoomOut"),
  btnZoomFit: document.getElementById("btnZoomFit"),
  btnGridToggle: document.getElementById("btnGridToggle"),
  zoomLabel: document.getElementById("zoomLabel"),
  measWidth: document.getElementById("measWidth"),
  measHeight: document.getElementById("measHeight"),
  selWidth: document.getElementById("selWidth"),
  selHeight: document.getElementById("selHeight"),
  canvasWrap: document.getElementById("canvasWrap"),
  livePreview: document.getElementById("livePreview"),
  fontLoadStatus: document.getElementById("fontLoadStatus"),
};

// ---------- Fixed color palette (replaces the native OS color-wheel
// pickers everywhere in the app: text color, stroke color, gradient
// start/end, and vinyl sheet color) ----------
const SWATCH_PALETTE = [
  { hex: "#000000", name: "Black" },
  { hex: "#ffffff", name: "White" },
  { hex: "#535353", name: "Gray" },
  { hex: "#bec1c0", name: "Lite Gray" },
  { hex: "#7b0013", name: "Burgundy" },
  { hex: "#e21003", name: "Red" },
  { hex: "#ff5f15", name: "Orange" },
  { hex: "#ffb400", name: "Amber" },
  { hex: "#fffd00", name: "Yellow" },
  { hex: "#04573d", name: "Forest Green" },
  { hex: "#009236", name: "Green" },
  { hex: "#00a6a6", name: "Teal" },
  { hex: "#0080db", name: "Blue" },
  { hex: "#032854", name: "Navy" },
  { hex: "#f0257a", name: "Pink" },
  { hex: "#bb008c", name: "Magenta" },
  { hex: "#59287a", name: "Purple" },
  { hex: "#a6ea22", name: "Lime" },
  { hex: "#5cead0", name: "Aqua" },
  { hex: "#ff91d0", name: "Rose" },
  { hex: "#ff8d78", name: "Coral" },
  { hex: "#f4e3c1", name: "Cream" },
  { hex: "#4f3925", name: "Brown" },
];

const paletteWraps = {};

function refreshSwatchActive(wrap, hex) {
  const normalized = (hex || "").toLowerCase();
  wrap.querySelectorAll(".palette-swatch").forEach((btn) => {
    btn.classList.toggle("active", btn.dataset.color.toLowerCase() === normalized);
  });
}

// The native <input type="color"> stays in the DOM (hidden via CSS) as the
// single source of truth for .value — every existing app.js listener
// (applyToSelection, saveState, etc.) keeps working completely unchanged,
// since a swatch click just sets that hidden input's value and dispatches
// the same "input"/"change" events the OS color-wheel used to fire.
function buildColorSwatches(inputEl) {
  if (!inputEl || inputEl._paletteWrap) return;
  inputEl.classList.add("palette-source-input");
  const wrap = document.createElement("div");
  wrap.className = "palette-swatches";
  wrap.setAttribute("role", "group");
  wrap.setAttribute("aria-label", "Color palette");
  SWATCH_PALETTE.forEach(({ hex, name }) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "palette-swatch";
    btn.style.background = hex;
    btn.dataset.color = hex;
    btn.title = name;
    btn.setAttribute("aria-label", "Color " + name);
    btn.addEventListener("click", () => {
      if (inputEl.disabled) return;
      inputEl.value = hex;
      inputEl.dispatchEvent(new Event("input", { bubbles: true }));
      inputEl.dispatchEvent(new Event("change", { bubbles: true }));
      refreshSwatchActive(wrap, hex);
      // Update color name display if it exists
      if (inputEl.id === "inputColor") {
        const nameDisplay = document.getElementById("colorNameDisplay");
        if (nameDisplay) nameDisplay.textContent = name;
      }
    });
    wrap.appendChild(btn);
  });
  inputEl.insertAdjacentElement("afterend", wrap);
  inputEl._paletteWrap = wrap;
  paletteWraps[inputEl.id] = wrap;
  refreshSwatchActive(wrap, inputEl.value);
}

// Re-syncs every swatch group's highlighted swatch to its input's CURRENT
// value — needed because several places in this file set el.value directly
// (e.g. picking up an existing object's color on selection) rather than
// through a swatch click, and setting the .value PROPERTY on an <input>
// does not trigger a MutationObserver on the "value" attribute.
function refreshAllPaletteSwatches() {
  Object.keys(paletteWraps).forEach((id) => {
    const input = document.getElementById(id);
    if (input) refreshSwatchActive(paletteWraps[id], input.value);
  });
}

[
  els.inputColor, 
  els.inputStrokeColor, 
  els.inputGradientColor1, 
  els.inputGradientColor2, 
  els.inputSheetColor,
  document.getElementById('inputClipartFill'),
  document.getElementById('inputClipartStrokeColor')
]
  .filter(Boolean) // Remove any null/undefined elements
  .forEach(buildColorSwatches);

// Build unified color palette bar
const unifiedFillInput = document.createElement('input');
unifiedFillInput.type = 'color';
unifiedFillInput.id = 'unifiedFillColor';
unifiedFillInput.value = '#000000';
unifiedFillInput.style.display = 'none';
document.body.appendChild(unifiedFillInput);
buildColorSwatches(unifiedFillInput);
// Move the palette into the unified swatches container
const unifiedSwatchContainer = document.getElementById('unifiedColorSwatches');
if (unifiedSwatchContainer && unifiedFillInput._paletteWrap) {
  unifiedSwatchContainer.appendChild(unifiedFillInput._paletteWrap);
}

const unifiedStrokeInput = document.createElement('input');
unifiedStrokeInput.type = 'color';
unifiedStrokeInput.id = 'unifiedStrokeColor';
unifiedStrokeInput.value = '#000000';
unifiedStrokeInput.style.display = 'none';
document.body.appendChild(unifiedStrokeInput);
buildColorSwatches(unifiedStrokeInput);
// Move the palette into the unified stroke swatches container
const unifiedStrokeSwatchContainer = document.getElementById('unifiedStrokeColorSwatches');
if (unifiedStrokeSwatchContainer && unifiedStrokeInput._paletteWrap) {
  unifiedStrokeSwatchContainer.appendChild(unifiedStrokeInput._paletteWrap);
}

// Sheet dimensions come straight from the Height/Length number fields
// (clamped to the 48"/96" hard caps), not a preset dropdown.
let backingW = clamp(parseFloat(els.inputSheetWidth.value) || 24, 1, SHEET_WIDTH_MAX) * PPI;
let backingH = clamp(parseFloat(els.inputSheetHeight.value) || 12, 1, SHEET_HEIGHT_MAX) * PPI;
let currentZoom = 1;

// Initialize sheet background color
const initialSheetColor = (els.inputSheetColor && els.inputSheetColor.value) || "#ffffff";
console.log("Initializing canvas with background color:", initialSheetColor);

const canvas = new fabric.Canvas("c", {
  width: backingW,
  height: backingH,
  backgroundColor: initialSheetColor,
  preserveObjectStacking: true,
  selection: false, // Disable drag-select rectangle
});

// FORCE selection to remain disabled - override any re-enabling
canvas.selection = false;
Object.defineProperty(canvas, 'selection', {
  get: function() { return false; },
  set: function(value) { /* ignore attempts to enable */ }
});

// Ensure background color is applied immediately
console.log("Canvas backgroundColor after creation:", canvas.backgroundColor);
canvas.renderAll();

// Force every letter object to scale proportionally only: hide the
// non-uniform edge handles (top/bottom/left/right) and lock uniform scaling
// so the shift-key override can't distort it either. Leaving strokeUniform
// at its default (false) is what makes stroke width scale WITH the object
// instead of staying a fixed pixel thickness — exactly what keeps an
// outline proportional as the letter is resized.
function lockUniformScaling(obj) {
  obj.lockUniScaling = true;
  obj.strokeUniform = false;
  obj.setControlsVisibility({ ml: false, mr: false, mt: false, mb: false });
  // Zoom here is a CSS transform (see applyZoom below), which Fabric has no
  // knowledge of — without this, its corner handles/border are always drawn
  // at a fixed canvas-space size, so on a large sheet (zoomed way out to
  // fit) they shrink to a couple of screen pixels and become nearly
  // impossible to see or grab. This keeps them a constant, always-clickable
  // size on screen no matter the zoom level.
  applyControlScaleForZoom(currentZoom, obj);
}

// ---------- Control handle sizing (compensates for the CSS-transform zoom) ----------
const CONTROL_SCREEN_SIZE = 14;        // corner-handle visual size on screen, in px
const CONTROL_TOUCH_SCREEN_SIZE = 30;  // invisible touch/click target, in px (bigger than visual)
const BORDER_SCREEN_WIDTH = 2;         // selection border thickness on screen, in px

// Pass a single object to size just that one (e.g. right after creating it),
// or omit it to resize every object currently on the canvas (e.g. whenever
// the zoom level itself changes).
// Fabric's default corners are HOLLOW (transparentCorners: true — just a
// thin outline), which all but disappears at small effective size or
// against a busy/light vinyl sheet color. Solid, opaque handles with a
// dark outline stay visible against any sheet color.
const CONTROL_FILL_COLOR = "#5eead4";
const CONTROL_STROKE_COLOR = "#0f1115";

function applyControlScaleForZoom(zoom, singleObject) {
  const z = Math.max(zoom || 1, 0.02);
  const cornerSize = CONTROL_SCREEN_SIZE / z;
  const touchCornerSize = CONTROL_TOUCH_SCREEN_SIZE / z;
  const borderScaleFactor = BORDER_SCREEN_WIDTH / z;

  // New objects created after this point inherit these as their defaults.
  fabric.Object.prototype.cornerSize = cornerSize;
  fabric.Object.prototype.touchCornerSize = touchCornerSize;
  fabric.Object.prototype.borderScaleFactor = borderScaleFactor;
  fabric.Object.prototype.transparentCorners = false;
  fabric.Object.prototype.cornerColor = CONTROL_FILL_COLOR;
  fabric.Object.prototype.cornerStrokeColor = CONTROL_STROKE_COLOR;

  const targets = singleObject ? [singleObject] : canvas.getObjects();
  targets.forEach((o) => {
    o.cornerSize = cornerSize;
    o.touchCornerSize = touchCornerSize;
    o.borderScaleFactor = borderScaleFactor;
    o.transparentCorners = false;
    o.cornerColor = CONTROL_FILL_COLOR;
    o.cornerStrokeColor = CONTROL_STROKE_COLOR;
  });
  canvas.requestRenderAll();
}

function debounce(fn, ms) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

// ---------- Font preloading ----------
// Explicitly fetch + decode every local TTF via the FontFace API and wait
// for them all before the app becomes interactive. This is the deterministic
// alternative to relying on lazy CSS @font-face loading / a CDN <link>.
function preloadFonts() {
  // System fonts (file: null) need no preloading — the browser resolves
  // them from whatever's already installed on the OS.
  const loaders = FONT_FILES.filter((f) => f.file).map((f) => {
    const face = new FontFace(f.family, `url("${f.file}")`, { weight: f.weight && f.weight !== "normal" ? f.weight : "400" });
    return face
      .load()
      .then((loaded) => {
        document.fonts.add(loaded);
      })
      .catch((err) => {
        console.warn("Font failed to preload:", f.family, err);
      });
  });
  return Promise.all(loaders);
}

function setUiEnabled(enabled) {
  els.btnAddText.disabled = !enabled;
  els.selectFont.disabled = !enabled;
}

// ---------- Zoom ----------
// Implemented as a pure CSS transform on a dedicated stage wrapper, fully
// decoupled from Fabric's own canvas backstore/dimensions. This guarantees
// zoom always visibly works regardless of Fabric's internal CSS-dimension
// quirks, and never disturbs the real (inch-accurate) design coordinates.
const canvasStage = document.createElement("div");
canvasStage.id = "canvasStage";
canvas.wrapperEl.parentNode.insertBefore(canvasStage, canvas.wrapperEl);
canvasStage.appendChild(canvas.wrapperEl);
canvas.wrapperEl.style.position = "absolute";
canvas.wrapperEl.style.top = "0";
canvas.wrapperEl.style.left = "0";
canvas.wrapperEl.style.transformOrigin = "top left";

function applyZoom(zoom) {
  currentZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
  canvas.wrapperEl.style.transform = `scale(${currentZoom})`;
  canvasStage.style.width = Math.round(backingW * currentZoom) + "px";
  canvasStage.style.height = Math.round(backingH * currentZoom) + "px";
  els.zoomLabel.textContent = Math.round(currentZoom * 100) + "%";
  canvas.calcOffset();
  // Re-scale every object's selection handles so they stay a constant,
  // clickable size on screen regardless of how zoomed out we are (see
  // lockUniformScaling() for the full explanation).
  applyControlScaleForZoom(currentZoom);
}

function computeFitZoom() {
  const wrap = els.canvasWrap;
  const style = window.getComputedStyle(wrap);
  const paddingLeft = parseFloat(style.paddingLeft) || 0;
  const paddingRight = parseFloat(style.paddingRight) || 0;
  const paddingTop = parseFloat(style.paddingTop) || 0;
  const paddingBottom = parseFloat(style.paddingBottom) || 0;
  const marginLeft = parseFloat(style.marginLeft) || 0;

  // Account for margin-left shift by doubling it (space on left + equivalent space needed on right)
  const availW = wrap.clientWidth - paddingLeft - paddingRight - (marginLeft * 2);
  const availH = wrap.clientHeight - paddingTop - paddingBottom;
  return Math.min(availW / backingW, availH / backingH, 1.5);
}

function zoomToFit() {
  applyZoom(computeFitZoom());
}

function zoomIn() {
  applyZoom(currentZoom * 1.15);
}

function zoomOut() {
  applyZoom(currentZoom / 1.15);
}

els.btnZoomIn.addEventListener("click", zoomIn);
els.btnZoomOut.addEventListener("click", zoomOut);
els.btnZoomFit.addEventListener("click", zoomToFit);

// Expose zoomToFit globally for sidebar toggle
window.zoomToFit = zoomToFit;

// ---------- Alignment Grid ----------
let gridEnabled = false;
const gridSpacingInches = 1; // 1 inch grid
const gridSpacingPx = gridSpacingInches * PPI;

function drawGrid() {
  if (!gridEnabled) {
    canvas.overlayImage = null;
    canvas.renderAll();
    return;
  }

  // Create grid pattern
  const gridCanvas = document.createElement('canvas');
  gridCanvas.width = canvas.width;
  gridCanvas.height = canvas.height;
  const ctx = gridCanvas.getContext('2d');

  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;

  ctx.lineWidth = 1;

  // Draw vertical lines (blue for regular, red for center)
  ctx.strokeStyle = 'rgba(59, 130, 246, 0.3)'; // blue with transparency
  for (let x = gridSpacingPx; x < canvas.width; x += gridSpacingPx) {
    if (Math.abs(x - centerX) < 1) continue; // skip center, will draw it separately
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, canvas.height);
    ctx.stroke();
  }

  // Draw horizontal lines (blue for regular, red for center)
  for (let y = gridSpacingPx; y < canvas.height; y += gridSpacingPx) {
    if (Math.abs(y - centerY) < 1) continue; // skip center, will draw it separately
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(canvas.width, y);
    ctx.stroke();
  }

  // Draw center lines in red
  ctx.strokeStyle = 'rgba(239, 68, 68, 0.5)'; // red with transparency
  ctx.lineWidth = 1.5;

  // Vertical center line
  ctx.beginPath();
  ctx.moveTo(centerX, 0);
  ctx.lineTo(centerX, canvas.height);
  ctx.stroke();

  // Horizontal center line
  ctx.beginPath();
  ctx.moveTo(0, centerY);
  ctx.lineTo(canvas.width, centerY);
  ctx.stroke();

  // Set as overlay
  fabric.Image.fromURL(gridCanvas.toDataURL(), function(img) {
    img.set({
      left: 0,
      top: 0,
      selectable: false,
      evented: false
    });
    canvas.overlayImage = img;
    canvas.renderAll();
  });
}

els.btnGridToggle.addEventListener("click", function() {
  gridEnabled = !gridEnabled;
  this.style.backgroundColor = gridEnabled ? 'rgba(59, 130, 246, 0.2)' : '';
  this.style.color = gridEnabled ? '#3b82f6' : '';
  drawGrid();
});
window.addEventListener("resize", debounce(zoomToFit, 150));

// ---------- Sheet size (Height/Length fields + Flip) ----------
function applySheetSize() {
  const h = clamp(parseFloat(els.inputSheetHeight.value) || 1, 1, SHEET_HEIGHT_MAX);
  const l = clamp(parseFloat(els.inputSheetWidth.value) || 1, 1, SHEET_WIDTH_MAX);
  els.inputSheetHeight.value = h;
  els.inputSheetWidth.value = l;
  backingH = h * PPI;
  backingW = l * PPI;
  canvas.setWidth(backingW);
  canvas.setHeight(backingH);
  canvas.renderAll();
  drawGrid(); // Redraw grid to match new canvas size
  zoomToFit();
  updateMeasurementsDebounced();
  saveState();
}

els.inputSheetHeight.addEventListener("change", applySheetSize);
els.inputSheetWidth.addEventListener("change", applySheetSize);

// ---------- "Edit this design" restore (from a saved cart item) ----------
// When embedded on the product page with ?vld_edit_cart_item=<key> in the
// URL, the plugin (class-vld-product-template.php) posts that cart item's
// saved vld_design_json payload into this iframe via postMessage — the
// SAME simplified JSON shape embed-bridge.js's buildPayload() produces
// (NOT Fabric's own canvas.toJSON() format), so it's rebuilt here letter
// by letter with fabric.Text objects rather than canvas.loadFromJSON().
let vldFontsReady = false;
let vldEditDesignPayload = null;
let vldEditDesignApplied = false;

// Gradient-filled letters are saved with `color` as the serialized
// fabric.Gradient object itself (type/coords/colorStops — see
// buildGradientFill()), NOT a plain hex string, since embed-bridge.js's
// buildPayload() just reads `o.fill` directly. Rebuilding a real
// fabric.Gradient from that saved shape (rather than falling back to a
// flat solid color) is what lets gradient-filled letters restore
// correctly — including re-populating the gradient color pickers and
// direction dropdown, since updateSelectedMeasurements() already expects
// obj.fill to be an actual object with .colorStops/.coords when the
// restored letter is selected.
function resolveFillFromSavedColor(savedColor) {
  if (typeof savedColor === "string") return savedColor;
  if (savedColor && Array.isArray(savedColor.colorStops) && savedColor.colorStops.length) {
    return new fabric.Gradient({
      type: savedColor.type || "linear",
      gradientUnits: savedColor.gradientUnits || "percentage",
      coords: savedColor.coords || gradientDirectionCoords("vertical"),
      colorStops: savedColor.colorStops.map((stop) => ({
        offset: stop.offset,
        color: stop.color,
      })),
    });
  }
  return "#111111"; // unrecognized shape — safe solid fallback, never a hard failure
}

function applyEditDesignPayload(payload) {
  // Clear the seed/example letter (or anything else already on the canvas)
  // before restoring the saved design.
  canvas.getObjects().slice().forEach((o) => canvas.remove(o));

  if (payload.sheet) {
    if (payload.sheet.heightIn) els.inputSheetHeight.value = payload.sheet.heightIn;
    if (payload.sheet.lengthIn) els.inputSheetWidth.value = payload.sheet.lengthIn;
    applySheetSize(); // recomputes backingW/backingH and resizes the canvas
    if (payload.sheet.color) {
      els.inputSheetColor.value = payload.sheet.color;
      canvas.backgroundColor = payload.sheet.color;
      refreshAllPaletteSwatches();
    }
  }
  els.inputIncludeSheet.checked = !!payload.includesSheet;

  (payload.objects || []).forEach((o) => {
    const fill = resolveFillFromSavedColor(o.color);
    const text = new fabric.Text(o.text || "TEXT", {
      fontFamily: o.fontFamily || DEFAULT_FONT_FILES[0].family,
      fontWeight: o.fontWeight || fontWeightFor(o.fontFamily),
      fontStyle: o.italic ? "italic" : "normal",
      fill,
      fontSize: 100,
      charSpacing: o.charSpacing || 0,
      stroke: o.stroke || null,
      strokeWidth: o.strokeWidth || 0,
      left: o.left,
      top: o.top,
      angle: o.angle || 0,
      originX: "center",
      originY: "center",
    });
    text.scale(o.scale || 1);
    lockUniformScaling(text);
    canvas.add(text);
  });

  canvas.renderAll();
  zoomToFit();
  updateMeasurementsDebounced();
  const firstObj = canvas.getObjects()[0];
  if (firstObj) {
    canvas.setActiveObject(firstObj);
    updateSelectedMeasurements(firstObj);
  } else {
    clearSelectedMeasurements();
  }
  updateLivePreview();
  saveState();
}

window.addEventListener("message", (event) => {
  const data = event.data;
  if (!data || data.type !== "vinyl-load-design" || vldEditDesignApplied) return;
  try {
    const payload = typeof data.design === "string" ? JSON.parse(data.design) : data.design;
    if (payload && Array.isArray(payload.objects) && payload.objects.length) {
      vldEditDesignPayload = payload;
      if (vldFontsReady) {
        applyEditDesignPayload(payload);
        vldEditDesignApplied = true;
      }
    }
  } catch (e) {
    console.warn("[VLD] failed to parse incoming design-to-edit payload", e);
  }
});

// Flip swaps the current Height/Length values (rotating the sheet 90°),
// clamping each into its field's own max in case the swapped value would
// otherwise exceed it (e.g. a 90" length can't become a 90" height, capped
// at 48").
els.inputFlip.addEventListener("change", () => {
  const hVal = parseFloat(els.inputSheetHeight.value) || 12;
  const lVal = parseFloat(els.inputSheetWidth.value) || 24;
  els.inputSheetHeight.value = clamp(lVal, 1, SHEET_HEIGHT_MAX);
  els.inputSheetWidth.value = clamp(hVal, 1, SHEET_WIDTH_MAX);
  applySheetSize();
});

// Vinyl sheet color is purely a canvas backdrop (and, optionally, part of
// the exported SVG — see Export Options). Not part of undo history since
// it's a sheet-level cosmetic setting, not a lettering edit.
els.inputSheetColor.addEventListener("input", () => {
  canvas.backgroundColor = els.inputSheetColor.value;
  canvas.renderAll();
  updateLivePreview();
});

// "Include the full vinyl sheet" changes what's actually being priced/cut
// (see updateMeasurements() below, and computeSqft() in embed-bridge.js
// for the WooCommerce embed) — so toggling it must immediately refresh the
// live measurements/price, not just wait for the next unrelated edit.
if (els.inputIncludeSheet) {
  els.inputIncludeSheet.addEventListener("change", () => {
    updateMeasurementsDebounced();
    saveState();
  });
}

// ---------- Pixel-accurate ink measurement ----------
// Fabric's getBoundingRect()/getScaledHeight() measure the object's transform
// box, which for text includes font-metric padding (ascent/descent/line-height)
// far beyond the visible ink. These helpers scan actual rendered alpha instead,
// so measurements match the real cut/visible lettering, not the invisible box.
const ALPHA_THRESHOLD = 10;

function scanAlphaBounds(ctx, w, h) {
  const data = ctx.getImageData(0, 0, w, h).data;
  let minX = w, minY = h, maxX = -1, maxY = -1;
  for (let y = 0; y < h; y++) {
    const rowStart = y * w;
    for (let x = 0; x < w; x++) {
      const a = data[(rowStart + x) * 4 + 3];
      if (a > ALPHA_THRESHOLD) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  return { minX, minY, maxX, maxY };
}

// Tight ink size (in canvas px) of a single object, ignoring its font-metric box.
// Always forces a fresh render first so a just-changed font/text is reflected.
// Includes stroke pixels — the outline is part of what actually gets cut.
function getObjectInkSizePx(obj) {
  obj.dirty = true;
  const multiplier = 3; // supersample for sub-pixel accuracy
  const el = obj.toCanvasElement({ multiplier, enableRetinaScaling: false });
  if (!el.width || !el.height) return { width: 0, height: 0 };
  const ctx = el.getContext("2d");
  const bounds = scanAlphaBounds(ctx, el.width, el.height);
  if (!bounds) return { width: 0, height: 0 };
  return {
    width: (bounds.maxX - bounds.minX + 1) / multiplier,
    height: (bounds.maxY - bounds.minY + 1) / multiplier,
  };
}

// Tight ink bounding box (in canvas px) of the entire design, ignoring the
// sheet background and any font-metric padding of individual objects.
function getWholeDesignInkBoundsPx() {
  if (canvas.getObjects().length === 0) return null;
  const originalBg = canvas.backgroundColor;
  canvas.backgroundColor = null;
  canvas.renderAll();
  const el = canvas.toCanvasElement(1, { enableRetinaScaling: false });
  canvas.backgroundColor = originalBg;
  canvas.renderAll();
  const ctx = el.getContext("2d");
  return scanAlphaBounds(ctx, el.width, el.height);
}

// Scale an object (uniformly) so its real ink height equals desiredInches.
// Re-measures ink fresh each time, so it's always correct for whatever font
// is currently applied — not the font that was active when it was first sized.
function scaleObjectToInkHeight(obj, desiredInches) {
  const ink = getObjectInkSizePx(obj);
  if (!ink || ink.height <= 0) return;
  const currentScale = obj.scaleX || 1;
  const desiredPx = desiredInches * PPI;
  const newScale = (desiredPx / ink.height) * currentScale;
  obj.scale(newScale);
  obj.setCoords();
}

// ---------- Undo / Redo history ----------
const MAX_HISTORY = 60;
let undoStack = [];
let redoStack = [];
let isRestoringState = false;

function snapshotObjects() {
  return JSON.stringify(canvas.getObjects().map((o) => o.toObject(["id"])));
}

function saveState() {
  if (isRestoringState) return;
  const snapshot = snapshotObjects();
  if (undoStack.length && undoStack[undoStack.length - 1] === snapshot) return;
  undoStack.push(snapshot);
  if (undoStack.length > MAX_HISTORY) undoStack.shift();
  redoStack.length = 0;
  updateUndoRedoButtons();
}

function loadSnapshot(snapshot) {
  isRestoringState = true;
  canvas.discardActiveObject();
  canvas.getObjects().slice().forEach((o) => canvas.remove(o));
  const objects = JSON.parse(snapshot);
  const finish = () => {
    canvas.renderAll();
    isRestoringState = false;
    updateMeasurementsDebounced();
    clearSelectedMeasurements();
    updateLivePreview();
    updateUndoRedoButtons();
  };
  if (objects.length === 0) {
    finish();
    return;
  }
  fabric.util.enlivenObjects(objects, (enlivened) => {
    enlivened.forEach((o) => {
      lockUniformScaling(o);
      canvas.add(o);
    });
    finish();
  });
}

function undo() {
  if (undoStack.length <= 1) return;
  const current = undoStack.pop();
  redoStack.push(current);
  const prev = undoStack[undoStack.length - 1];
  loadSnapshot(prev);
}

function redo() {
  if (redoStack.length === 0) return;
  const snapshot = redoStack.pop();
  undoStack.push(snapshot);
  loadSnapshot(snapshot);
}

function updateUndoRedoButtons() {
  els.btnUndo.disabled = undoStack.length <= 1;
  els.btnRedo.disabled = redoStack.length === 0;
}

els.btnUndo.addEventListener("click", undo);
els.btnRedo.addEventListener("click", redo);

document.addEventListener("keydown", (e) => {
  const tag = (e.target && e.target.tagName) || "";
  if (tag === "INPUT" || tag === "TEXTAREA") return;
  const key = e.key.toLowerCase();
  if ((e.ctrlKey || e.metaKey) && key === "z" && !e.shiftKey) {
    e.preventDefault();
    undo();
  } else if ((e.ctrlKey || e.metaKey) && (key === "y" || (key === "z" && e.shiftKey))) {
    e.preventDefault();
    redo();
  }
});

// ---------- Stroke controls ----------
function currentStrokeProps() {
  // Stroke controls removed - using unified bottom palette instead
  return {
    stroke: null,
    strokeWidth: 0,
  };
}

function setStrokeControlsEnabled(enabled) {
  // Stroke controls removed - using unified bottom palette instead
  // els.inputStrokeColor.disabled = !enabled;
  // els.inputStrokeWidth.disabled = !enabled;
}

// Stroke controls removed - using unified bottom palette instead
// els.inputStrokeEnabled.addEventListener("change", () => {
//   setStrokeControlsEnabled(els.inputStrokeEnabled.checked);
//   applyToSelection(currentStrokeProps());
//   saveState();
// });
// els.inputStrokeColor.addEventListener("input", () => applyToSelection(currentStrokeProps()));
// els.inputStrokeColor.addEventListener("change", () => saveState());
// els.inputStrokeWidth.addEventListener("input", () => applyToSelection(currentStrokeProps()));
// els.inputStrokeWidth.addEventListener("change", () => saveState());

// ---------- Gradient fill ----------
// A simple two-color linear gradient across the letter, replacing the solid
// text color when enabled. Uses gradientUnits:"percentage" (0-1 fractions
// of the object's own box) rather than raw pixel coordinates, so it keeps
// looking correct no matter how the letter is later resized.
function gradientDirectionCoords(direction) {
  if (direction === "horizontal") return { x1: 0, y1: 0, x2: 1, y2: 0 };
  if (direction === "diagonal") return { x1: 0, y1: 0, x2: 1, y2: 1 };
  return { x1: 0, y1: 0, x2: 0, y2: 1 }; // vertical (default)
}

function directionFromGradientCoords(coords) {
  if (!coords) return "vertical";
  if (coords.x2 === 1 && coords.y2 === 1) return "diagonal";
  if (coords.y1 === coords.y2) return "horizontal";
  return "vertical";
}

function buildGradientFill(obj) {
  // For groups, we need to use pixel coordinates instead of percentage
  if (obj && obj.type === 'group') {
    const bounds = obj.getBoundingRect();
    const direction = els.selectGradientDirection.value;
    let coords;

    if (direction === 'horizontal') {
      coords = { x1: 0, y1: bounds.height / 2, x2: bounds.width, y2: bounds.height / 2 };
    } else if (direction === 'diagonal') {
      coords = { x1: 0, y1: 0, x2: bounds.width, y2: bounds.height };
    } else { // vertical
      coords = { x1: bounds.width / 2, y1: 0, x2: bounds.width / 2, y2: bounds.height };
    }

    return new fabric.Gradient({
      type: "linear",
      coords: coords,
      colorStops: [
        { offset: 0, color: els.inputGradientColor1.value },
        { offset: 1, color: els.inputGradientColor2.value },
      ],
    });
  }

  // For text and single objects, use percentage
  return new fabric.Gradient({
    type: "linear",
    gradientUnits: "percentage",
    coords: gradientDirectionCoords(els.selectGradientDirection.value),
    colorStops: [
      { offset: 0, color: els.inputGradientColor1.value },
      { offset: 1, color: els.inputGradientColor2.value },
    ],
  });
}

function setGradientControlsEnabled(enabled) {
  els.inputGradientColor1.disabled = !enabled;
  els.inputGradientColor2.disabled = !enabled;
  els.selectGradientDirection.disabled = !enabled;
}

// Applies whichever fill currently belongs on an object — the gradient (if
// the checkbox is on) or the plain solid color picker (if not). Used both
// when toggling/editing gradient controls on a selected object AND right
// when a brand-new letter is created, so it respects the gradient state
// immediately instead of always starting solid.
function applyCurrentFillTo(obj) {
  const useGradient = els.inputGradientEnabled.checked;
  const fillValue = useGradient ? buildGradientFill(obj) : els.inputColor.value;

  if (obj.type === 'group') {
    // For groups (complex clipart), apply fill to each child
    // Don't use gradients on groups - causes coordinate issues
    obj.forEachObject((child) => {
      child.set({ fill: els.inputColor.value });
    });
  } else {
    // For single objects (text, simple shapes), gradients work fine
    obj.set({ fill: fillValue });
  }
  obj.dirty = true;
}

els.inputGradientEnabled.addEventListener("change", () => {
  const obj = canvas.getActiveObject();

  // Check if trying to apply gradient to a group
  if (els.inputGradientEnabled.checked && obj && obj.type === 'group') {
    alert('⚠️ Gradients are not supported on complex clipart with multiple paths.\n\nGradients work on:\n• Text\n• Simple shapes (circles, squares, etc.)\n\nUse solid colors from the palette below instead.');
    els.inputGradientEnabled.checked = false;
    return;
  }

  setGradientControlsEnabled(els.inputGradientEnabled.checked);
  if (obj) {
    applyCurrentFillTo(obj);
    canvas.renderAll();
    updateLivePreview();
    saveState();
  }
});
els.inputGradientColor1.addEventListener("input", () => {
  const obj = canvas.getActiveObject();
  if (obj && els.inputGradientEnabled.checked) {
    applyCurrentFillTo(obj);
    canvas.renderAll();
    updateLivePreview();
  }
});
els.inputGradientColor1.addEventListener("change", () => saveState());
els.inputGradientColor2.addEventListener("input", () => {
  const obj = canvas.getActiveObject();
  if (obj && els.inputGradientEnabled.checked) {
    applyCurrentFillTo(obj);
    canvas.renderAll();
    updateLivePreview();
  }
});
els.inputGradientColor2.addEventListener("change", () => saveState());
els.selectGradientDirection.addEventListener("change", () => {
  const obj = canvas.getActiveObject();
  if (obj && els.inputGradientEnabled.checked) {
    applyCurrentFillTo(obj);
    canvas.renderAll();
    updateLivePreview();
    saveState();
  }
});

// ---------- Layer order (bring to front / send to back) ----------
function setLayerButtonsEnabled(enabled) {
  els.btnBringFront.disabled = !enabled;
  els.btnSendBack.disabled = !enabled;
  els.btnBringForward.disabled = !enabled;
  els.btnSendBackward.disabled = !enabled;
  els.btnDelete.disabled = !enabled;
  els.btnCopy.disabled = !enabled;
  // btnPaste is intentionally NOT tied to selection state — it stays
  // enabled once something has been copied, regardless of what (if
  // anything) is currently selected.
}

els.btnBringFront.addEventListener("click", () => {
  const obj = canvas.getActiveObject();
  if (!obj) return;
  canvas.bringToFront(obj);
  canvas.renderAll();
  saveState();
});
els.btnSendBack.addEventListener("click", () => {
  const obj = canvas.getActiveObject();
  if (!obj) return;
  canvas.sendToBack(obj);
  canvas.renderAll();
  saveState();
});
els.btnBringForward.addEventListener("click", () => {
  const obj = canvas.getActiveObject();
  if (!obj) return;
  canvas.bringForward(obj);
  canvas.renderAll();
  saveState();
});
els.btnSendBackward.addEventListener("click", () => {
  const obj = canvas.getActiveObject();
  if (!obj) return;
  canvas.sendBackwards(obj);
  canvas.renderAll();
  saveState();
});

// ---------- Add text ----------
// Uses fabric.Text (NOT IText/Textbox) — plain Text objects are never
// editable in place, so double-clicking on the canvas can't summon a text
// cursor. The ONLY way to edit lettering content is the sidebar text box.
// Track offset for new text objects so they don't stack on top of each other
let textOffsetCounter = 0;

function addText() {
  const value = els.inputText.value.trim() || "TEXT";
  const heightInches = parseFloat(els.inputHeight.value);
  const fontFamily = els.selectFont.value;
  const fill = els.inputColor.value;
  const charSpacing = parseInt(els.inputSpacing.value, 10);
  const stroke = currentStrokeProps();

  // Offset each new text by 30 pixels (cascading effect)
  const offset = (textOffsetCounter % 10) * 30; // Reset after 10 objects
  textOffsetCounter++;

  const text = new fabric.Text(value, {
    fontFamily,
    fontWeight: fontWeightFor(fontFamily),
    fontStyle: els.inputItalic.checked ? "italic" : "normal",
    fill,
    fontSize: 100,
    charSpacing,
    stroke: stroke.stroke,
    strokeWidth: stroke.strokeWidth,
    left: backingW / 2 + offset,
    top: backingH / 2 + offset,
    originX: "center",
    originY: "center",
  });
  lockUniformScaling(text);
  applyCurrentFillTo(text); // respects the Gradient Fill toggle for brand-new letters too

  canvas.add(text);
  canvas.renderAll();

  scaleObjectToInkHeight(text, heightInches);

  canvas.setActiveObject(text);
  canvas.renderAll();
  updateMeasurementsDebounced();
  updateSelectedMeasurements(text);
  updateLivePreview();
  saveState();
}

// ---------- Add Shape ----------
let shapeOffsetCounter = 0;

function addShape(shapeType) {
  const fill = document.getElementById('inputShapeFill')?.value || '#3b82f6';
  const hasStroke = document.getElementById('inputShapeStroke')?.checked || false;
  const strokeColor = document.getElementById('inputShapeStrokeColor')?.value || '#000000';
  const strokeWidth = parseInt(document.getElementById('inputShapeStrokeWidth')?.value, 10) || 2;

  // Offset each new shape by 30 pixels (cascading effect)
  const offset = (shapeOffsetCounter % 10) * 30;
  shapeOffsetCounter++;

  let shape;
  const baseSize = 100; // Base size in pixels
  const centerX = backingW / 2 + offset;
  const centerY = backingH / 2 + offset;

  switch(shapeType) {
    case 'circle':
      shape = new fabric.Circle({
        radius: baseSize / 2,
        fill: fill,
        left: centerX - (baseSize / 2),
        top: centerY - (baseSize / 2),
      });
      break;

    case 'square':
      shape = new fabric.Rect({
        width: baseSize,
        height: baseSize,
        fill: fill,
        left: centerX - (baseSize / 2),
        top: centerY - (baseSize / 2),
      });
      break;

    case 'rectangle':
      shape = new fabric.Rect({
        width: baseSize * 1.5,
        height: baseSize,
        fill: fill,
        left: centerX - (baseSize * 1.5 / 2),
        top: centerY - (baseSize / 2),
      });
      break;

    case 'triangle':
      shape = new fabric.Triangle({
        width: baseSize,
        height: baseSize,
        fill: fill,
        left: centerX - (baseSize / 2),
        top: centerY - (baseSize / 2),
      });
      break;

    case 'star':
      // Create a 5-pointed star using polygon
      const starPoints = [];
      const spikes = 5;
      const outerRadius = baseSize / 2;
      const innerRadius = outerRadius * 0.4;

      for (let i = 0; i < spikes * 2; i++) {
        const radius = i % 2 === 0 ? outerRadius : innerRadius;
        const angle = (i * Math.PI) / spikes - Math.PI / 2;
        starPoints.push({
          x: radius * Math.cos(angle),
          y: radius * Math.sin(angle)
        });
      }

      shape = new fabric.Polygon(starPoints, {
        fill: fill,
        left: centerX - (baseSize / 2),
        top: centerY - (baseSize / 2),
      });
      break;

    case 'hexagon':
      // Create a hexagon using polygon
      const hexPoints = [];
      const hexRadius = baseSize / 2;

      for (let i = 0; i < 6; i++) {
        const angle = (i * Math.PI) / 3 - Math.PI / 2;
        hexPoints.push({
          x: hexRadius * Math.cos(angle),
          y: hexRadius * Math.sin(angle)
        });
      }

      shape = new fabric.Polygon(hexPoints, {
        fill: fill,
        left: centerX - (baseSize / 2),
        top: centerY - (baseSize / 2),
      });
      break;

    default:
      return;
  }

  // Add stroke if enabled
  if (hasStroke) {
    shape.set({
      stroke: strokeColor,
      strokeWidth: strokeWidth,
      strokeUniform: true  // Keep stroke width constant when scaling
    });
  }

  // Enable free transformation for shapes (non-proportional scaling)
  shape.set({
    lockUniScaling: false,
    lockScalingFlip: false,
    noScaleCache: false,
    strokeUniform: true  // Keep stroke width constant when scaling
  });

  canvas.add(shape);
  canvas.setActiveObject(shape);
  canvas.renderAll();
  updateMeasurementsDebounced();
  updateSelectedMeasurements(shape);
  updateLivePreview();
  saveState();
}

// ---------- Clipart Management ----------
let clipartOffsetCounter = 0;

async function addClipart(url, name) {
  try {
    const fill = document.getElementById('inputClipartFill')?.value || '#3b82f6';
    const hasStroke = document.getElementById('inputClipartStroke')?.checked || false;
    const strokeColor = document.getElementById('inputClipartStrokeColor')?.value || '#000000';
    const strokeWidth = parseInt(document.getElementById('inputClipartStrokeWidth')?.value, 10) || 2;

    // Load the SVG content
    const svgContent = await loadSvgContent(url);
    if (!svgContent) {
      console.error('Failed to load clipart:', name);
      return;
    }

    // Offset each new clipart by 30 pixels (cascading effect)
    const offset = (clipartOffsetCounter % 10) * 30;
    clipartOffsetCounter++;

    const baseSize = 150; // Base size in pixels
    const centerX = backingW / 2 + offset;
    const centerY = backingH / 2 + offset;

    // Load SVG using Fabric's loadSVGFromString
    fabric.loadSVGFromString(svgContent, (objects, options) => {
      if (!objects || objects.length === 0) {
        console.error('No objects loaded from SVG:', name);
        return;
      }

      const svgObj = fabric.util.groupSVGElements(objects, options);

      if (!svgObj) {
        console.error('Failed to group SVG elements:', name);
        return;
      }

      // Apply fill color - handle both single objects and groups
      if (svgObj.type === 'group') {
        svgObj.forEachObject((obj) => {
          obj.set({ fill: fill });
          if (hasStroke) {
            obj.set({
              stroke: strokeColor,
              strokeWidth: strokeWidth,
              strokeUniform: true
            });
          }
        });
      } else {
        // Single object
        svgObj.set({ fill: fill });
        if (hasStroke) {
          svgObj.set({
            stroke: strokeColor,
            strokeWidth: strokeWidth,
            strokeUniform: true
          });
        }
      }

      // Scale to base size
      const scale = baseSize / Math.max(svgObj.width || 100, svgObj.height || 100);
      svgObj.scale(scale);

      // Position at center
      svgObj.set({
        left: centerX,
        top: centerY,
        originX: 'center',
        originY: 'center',
        lockUniScaling: false,
        strokeUniform: true
      });

      canvas.add(svgObj);
      canvas.setActiveObject(svgObj);
      canvas.renderAll();
      updateMeasurementsDebounced();
      updateSelectedMeasurements(svgObj);
      updateLivePreview();
      saveState();
    });

  } catch (error) {
    console.error('Error adding clipart:', error);
  }
}

function initializeClipart() {
  const clipartContainer = document.getElementById('clipartCategories');
  if (!clipartContainer) return;

  try {
    const categories = getClipartCategories();
    const categoryKeys = Object.keys(categories).sort();

    if (categoryKeys.length === 0) {
      clipartContainer.innerHTML = `
        <div class="text-center text-gray-500 text-sm py-8">
          <p>No clipart found.</p>
          <p class="text-xs mt-2">Add SVG files to assets/clipart/ folders.</p>
        </div>
      `;
      return;
    }

    // Clear loading message
    clipartContainer.innerHTML = '';

    // Create category sections
    categoryKeys.forEach(categoryKey => {
      const items = categories[categoryKey];
      const categoryDiv = document.createElement('div');
      categoryDiv.className = 'clipart-category';

      const titleDiv = document.createElement('div');
      titleDiv.className = 'clipart-category-title';
      titleDiv.textContent = formatCategoryName(categoryKey);

      const gridDiv = document.createElement('div');
      gridDiv.className = 'clipart-grid';

      items.forEach(item => {
        const itemButton = document.createElement('button');
        itemButton.className = 'clipart-item';
        itemButton.type = 'button';
        itemButton.title = item.name;
        itemButton.innerHTML = `<img src="${item.url}" alt="${item.name}" />`;

        itemButton.addEventListener('click', () => {
          addClipart(item.url, item.name);
        });

        gridDiv.appendChild(itemButton);
      });

      categoryDiv.appendChild(titleDiv);
      categoryDiv.appendChild(gridDiv);
      clipartContainer.appendChild(categoryDiv);
    });

  } catch (error) {
    console.error('Error initializing clipart:', error);
    clipartContainer.innerHTML = `
      <div class="text-center text-red-500 text-sm py-8">
        Error loading clipart. Check console for details.
      </div>
    `;
  }
}

// ---------- Live edits to selected object ----------
function applyToSelection(props) {
  const obj = canvas.getActiveObject();
  if (!obj) return;
  obj.set(props);
  obj.setCoords();
  canvas.renderAll();
  updateMeasurementsDebounced();
  updateSelectedMeasurements(obj);
  updateLivePreview();
}

// Font changes can drastically change a font's ink-to-em ratio, so we
// re-derive the scale to preserve the height the user actually intended
// instead of leaving the old scale factor (which was the source of the
// "measurements go stale after a font swap" bug). We also explicitly set
// the exact preloaded weight for the new family so canvas resolves to the
// precise loaded FontFace instance instead of guessing.
els.selectFont.addEventListener("change", () => {
  const obj = canvas.getActiveObject();
  const fontFamily = els.selectFont.value;
  if (obj && obj.type === "text") {
    const targetHeight = parseFloat(els.inputHeight.value);
    obj.set({ fontFamily, fontWeight: fontWeightFor(fontFamily) });
    obj.dirty = true;
    canvas.renderAll();
    scaleObjectToInkHeight(obj, targetHeight);
    obj.setCoords();
    canvas.renderAll();
    updateMeasurementsDebounced();
    updateSelectedMeasurements(obj);
    updateLivePreview();
    saveState();
  }
});

els.inputItalic.addEventListener("change", () => {
  const obj = canvas.getActiveObject();
  if (obj && obj.type === "text") {
    const targetHeight = parseFloat(els.inputHeight.value);
    obj.set({ fontStyle: els.inputItalic.checked ? "italic" : "normal" });
    obj.dirty = true;
    canvas.renderAll();
    scaleObjectToInkHeight(obj, targetHeight);
    obj.setCoords();
    canvas.renderAll();
    updateMeasurementsDebounced();
    updateSelectedMeasurements(obj);
    updateLivePreview();
    saveState();
  }
});

els.inputColor.addEventListener("input", () => applyToSelection({ fill: els.inputColor.value }));
els.inputColor.addEventListener("change", () => saveState());
els.inputSpacing.addEventListener("input", () => applyToSelection({ charSpacing: parseInt(els.inputSpacing.value, 10) }));
els.inputSpacing.addEventListener("change", () => saveState());

// The sidebar text box is the ONLY place lettering content can be edited.
// While an object is selected, typing here live-updates its text content.
els.inputText.addEventListener("input", () => {
  const obj = canvas.getActiveObject();
  if (obj && obj.type === "text") {
    const targetHeight = parseFloat(els.inputHeight.value);
    obj.set({ text: els.inputText.value || "" });
    obj.dirty = true;
    canvas.renderAll();
    scaleObjectToInkHeight(obj, targetHeight);
    obj.setCoords();
    canvas.renderAll();
    updateMeasurementsDebounced();
    updateSelectedMeasurements(obj);
    updateLivePreview();
  }
});
els.inputText.addEventListener("change", () => {
  const obj = canvas.getActiveObject();
  if (obj && obj.type === "text") saveState();
});

els.inputHeight.addEventListener("input", () => {
  const val = parseFloat(els.inputHeight.value);
  els.heightVal.textContent = val.toFixed(2) + '"';
  const obj = canvas.getActiveObject();
  if (obj && obj.type === "text") {
    scaleObjectToInkHeight(obj, val);
    canvas.renderAll();
    updateMeasurementsDebounced();
    updateSelectedMeasurements(obj);
    updateLivePreview();
  }
});
els.inputHeight.addEventListener("change", () => saveState());

els.btnAddText.addEventListener("click", addText);
// Enter in the text box: if editing an existing selected object, just
// commit the edit (no cursor/editing on canvas to "confirm" into); if
// nothing is selected, Enter creates a brand new object instead.
els.inputText.addEventListener("keydown", (e) => {
  if (e.key !== "Enter") return;
  const obj = canvas.getActiveObject();
  if (obj && obj.type === "text") {
    saveState();
    els.inputText.blur();
  } else {
    addText();
  }
});

// Helper function to delete selected objects (single or multiple)
function deleteSelected() {
  const activeObj = canvas.getActiveObject();
  if (!activeObj) return;

  // If it's a multi-selection (ActiveSelection), remove all objects in it
  if (activeObj.type === "activeSelection") {
    activeObj.forEachObject((obj) => {
      canvas.remove(obj);
    });
  } else {
    // Single object
    canvas.remove(activeObj);
  }

  canvas.discardActiveObject();
  canvas.renderAll();
  updateMeasurementsDebounced();
  clearSelectedMeasurements();
  updateLivePreview();
  saveState();
}

els.btnDelete.addEventListener("click", deleteSelected);

// ---------- Copy / Paste ----------
// A simple in-memory clipboard (not the OS clipboard) — Copy stores a clone
// of the current selection, Paste adds another clone of whatever was last
// copied, offset slightly so it's visibly a new object rather than stacked
// exactly on top of the original.
let clipboardObject = null;

function copySelected() {
  const obj = canvas.getActiveObject();
  if (!obj) return;

  // ONLY support single object copy/paste
  // Multi-object copy in Fabric.js is fundamentally broken
  if (obj.type === "activeSelection") {
    alert("Please select only one object at a time to copy.");
    canvas.discardActiveObject();
    canvas.requestRenderAll();
    return;
  }

  // Single object - serialize to JSON
  clipboardObject = obj.toObject();
  els.btnPaste.disabled = false;
}

function pasteClipboard() {
  if (!clipboardObject) return;
  
  // Only single object paste supported (multi-paste disabled in copySelected)
  if (Array.isArray(clipboardObject)) {
    alert("Multi-object paste is not supported. Please copy one object at a time.");
    return;
  }

  // Single object paste from JSON
  fabric.util.enlivenObjects([clipboardObject], (objects) => {
    const obj = objects[0];
    obj.set({
      left: (clipboardObject.left || 0) + 20,
      top: (clipboardObject.top || 0) + 20,
    });
    lockUniformScaling(obj);
    
    // Clear selection first to ensure object goes to canvas
    canvas.discardActiveObject();
    
    canvas.add(obj);
    canvas.setActiveObject(obj);
    canvas.renderAll();
    updateMeasurementsDebounced();
    updateSelectedMeasurements(obj);
    updateLivePreview();
    saveState();
    
    // Update clipboard to JSON of new pasted object
    clipboardObject = obj.toObject();
  });
}

els.btnCopy.addEventListener("click", copySelected);
els.btnPaste.addEventListener("click", pasteClipboard);

document.addEventListener("keydown", (e) => {
  const tag = (e.target && e.target.tagName) || "";
  if (tag === "INPUT" || tag === "TEXTAREA") return;
  const key = e.key.toLowerCase();
  if ((e.ctrlKey || e.metaKey) && key === "c") {
    e.preventDefault();
    copySelected();
  } else if ((e.ctrlKey || e.metaKey) && key === "v") {
    e.preventDefault();
    pasteClipboard();
  } else if (key === "delete" || key === "backspace") {
    e.preventDefault();
    deleteSelected();
  }
});

els.btnCenterAll.addEventListener("click", () => {
  const objects = canvas.getObjects();
  if (objects.length === 0) return;

  // Calculate bounding box of all objects
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  objects.forEach(obj => {
    const bound = obj.getBoundingRect();
    minX = Math.min(minX, bound.left);
    minY = Math.min(minY, bound.top);
    maxX = Math.max(maxX, bound.left + bound.width);
    maxY = Math.max(maxY, bound.top + bound.height);
  });

  // Calculate center of all objects
  const groupCenterX = (minX + maxX) / 2;
  const groupCenterY = (minY + maxY) / 2;

  // Calculate canvas center
  const canvasCenterX = canvas.width / 2;
  const canvasCenterY = canvas.height / 2;

  // Calculate offset to move group to canvas center
  const offsetX = canvasCenterX - groupCenterX;
  const offsetY = canvasCenterY - groupCenterY;

  // Move all objects
  objects.forEach(obj => {
    obj.set({
      left: obj.left + offsetX,
      top: obj.top + offsetY
    });
    obj.setCoords();
  });

  canvas.renderAll();
  updateMeasurementsDebounced();
  checkBoundsWarningDebounced();
  updateLivePreview();
  saveState();
});

els.btnClear.addEventListener("click", () => {
  if (!confirm("Clear all objects from the canvas? This cannot be undone.")) return;
  canvas.getObjects().slice().forEach((o) => canvas.remove(o));
  canvas.renderAll();
  updateMeasurementsDebounced();
  clearSelectedMeasurements();
  updateLivePreview();
  saveState();
});

// ==================================================================
// Export Button Handlers
// ==================================================================

// Add to Design button (sends to WooCommerce)
const btnAddToDesign = document.getElementById("btnAddToDesign");
if (btnAddToDesign) {
  btnAddToDesign.addEventListener("click", () => {
    if (canvas.getObjects().length === 0) {
      alert("Please add some content to your design first!");
      return;
    }
    sendDesignToWooCommerce();
  });

  // Enable button when there are objects on canvas
  canvas.on("object:added", () => {
    btnAddToDesign.disabled = canvas.getObjects().length === 0;
  });
  canvas.on("object:removed", () => {
    btnAddToDesign.disabled = canvas.getObjects().length === 0;
  });
}

// Download SVG button
const btnDownloadSvg = document.getElementById("btnDownloadSvg");
if (btnDownloadSvg) {
  btnDownloadSvg.addEventListener("click", () => {
    if (canvas.getObjects().length === 0) {
      alert("Please add some content to your design first!");
      return;
    }
    downloadSvg();
  });
}

// Download PNG button
const btnDownloadPng = document.getElementById("btnDownloadPng");
if (btnDownloadPng) {
  btnDownloadPng.addEventListener("click", () => {
    if (canvas.getObjects().length === 0) {
      alert("Please add some content to your design first!");
      return;
    }
    downloadPng();
  });
}

// ==================================================================
// Upload Handler
// ==================================================================

const btnUpload = document.getElementById("btnUpload");
const uploadFileInput = document.getElementById("uploadFileInput");

if (btnUpload && uploadFileInput) {
  btnUpload.addEventListener("click", () => {
    uploadFileInput.click();
  });

  uploadFileInput.addEventListener("change", (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size
    const maxSize = file.name.endsWith('.svg') ? 3 * 1024 * 1024 : 10 * 1024 * 1024; // 3MB for SVG, 10MB for images
    if (file.size > maxSize) {
      alert(`File too large! Max size: ${file.name.endsWith('.svg') ? '3MB for SVG' : '10MB for images'}`);
      uploadFileInput.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target.result;

      if (file.type.startsWith('image/svg')) {
        // Load SVG
        fabric.loadSVGFromString(dataUrl, (objects, options) => {
          const svgGroup = fabric.util.groupSVGElements(objects, options);
          svgGroup.set({
            left: backingW / 2,
            top: backingH / 2,
            originX: 'center',
            originY: 'center',
            isUploadedGraphic: true // Mark for export
          });

          // Scale to fit if too large
          const maxDim = Math.min(backingW, backingH) * 0.5;
          if (svgGroup.width > maxDim || svgGroup.height > maxDim) {
            const scale = maxDim / Math.max(svgGroup.width, svgGroup.height);
            svgGroup.scale(scale);
          }

          // Lock uniform scaling (aspect ratio) and hide edge handles
          lockUniformScaling(svgGroup);

          canvas.add(svgGroup);
          canvas.setActiveObject(svgGroup);
          canvas.renderAll();
          saveState();
        });
      } else {
        // Load raster image (JPEG, PNG, WebP, HEIC)
        fabric.Image.fromURL(dataUrl, (img) => {
          img.set({
            left: backingW / 2,
            top: backingH / 2,
            originX: 'center',
            originY: 'center',
            isUploadedGraphic: true // Mark for export
          });

          // Scale to fit if too large
          const maxDim = Math.min(backingW, backingH) * 0.5;
          if (img.width > maxDim || img.height > maxDim) {
            const scale = maxDim / Math.max(img.width, img.height);
            img.scale(scale);
          }

          // Lock uniform scaling (aspect ratio) and hide edge handles
          lockUniformScaling(img);

          canvas.add(img);
          canvas.setActiveObject(img);
          canvas.renderAll();
          saveState();
        });
      }
    };

    // Read file based on type
    if (file.type.startsWith('image/svg')) {
      reader.readAsText(file);
    } else {
      reader.readAsDataURL(file);
    }

    // Reset input
    uploadFileInput.value = '';
  });
}

// ---------- Build SVG string ----------
// Shared by the "Save as SVG" button below AND the WooCommerce embed bridge
// (embed-bridge.js), which posts this exact string up to the parent page so
// the real cut-ready file — not just a PNG preview — gets attached to the
// WooCommerce order for fulfillment. Keeping ONE function means the SVG a
// customer downloads and the SVG that lands on the order are guaranteed
// identical.
//
// "includeSheet" toggles whether the sheet's background rectangle (at its
// current color) is baked into the SVG, or the export is just the bare
// lettering paths (the common case for sending to a cutter).
function buildDesignSvg(includeSheet) {
  const originalBg = canvas.backgroundColor;
  if (!includeSheet) canvas.backgroundColor = null;

  // width/height must carry an explicit physical unit (inches) — passing
  // raw pixel counts left the receiving app to guess a DPI (commonly 96),
  // which scaled the real-world size wrong. The viewBox keeps our internal
  // 50px-per-inch coordinate system mapped onto that physical size exactly.
  const lengthInches = backingW / PPI;
  const heightInches = backingH / PPI;

  // Build SVG manually from individual objects and flatten transforms
  const objects = canvas.getObjects();
  let objectsSvg = '';
  let imagesSvg = ''; // Images handled separately with 1.92 scaling

  for (let obj of objects) {
    let svgString = obj.toSVG();

    // Flatten transform matrices for shapes and images
    // CorelDRAW doesn't handle Fabric's <g transform="matrix(...)"> wrappers correctly
    if (['rect', 'circle', 'triangle', 'polygon', 'image'].includes(obj.type)) {
      const matrixMatch = svgString.match(/transform="matrix\(([\d.\-\s]+)\)"/);

      if (matrixMatch) {
        const [, matrixStr] = matrixMatch;
        const [scaleX, , , scaleY, translateX, translateY] = matrixStr.split(/\s+/).map(parseFloat);

        if (obj.type === 'image') {
          // For images, extract x, y, width, height and apply transforms + 1.92 scale
          const imageMatch = svgString.match(/x="([\d.\-]+)"\s+y="([\d.\-]+)"\s+width="([\d.\-]+)"\s+height="([\d.\-]+)"/);
          if (imageMatch) {
            const x = parseFloat(imageMatch[1]);
            const y = parseFloat(imageMatch[2]);
            const width = parseFloat(imageMatch[3]);
            const height = parseFloat(imageMatch[4]);

            // Apply transform matrix AND 1.92 scale factor for CorelDRAW 96 DPI
            const scaleFactor = 1.92;
            const finalX = Math.round((translateX + (x * scaleX)) * scaleFactor * 100) / 100;
            const finalY = Math.round((translateY + (y * scaleY)) * scaleFactor * 100) / 100;
            const finalWidth = Math.round((width * scaleX) * scaleFactor * 100) / 100;
            const finalHeight = Math.round((height * scaleY) * scaleFactor * 100) / 100;

            // Remove the <g> wrapper and transform attribute
            svgString = svgString.replace(/<g[^>]*>/, '');
            svgString = svgString.replace(/<\/g>/, '');
            svgString = svgString.replace(/\s*vector-effect="non-scaling-stroke"/, '');

            // Update image attributes with flattened values
            svgString = svgString.replace(/x="[\d.\-]+"/, `x="${finalX}"`);
            svgString = svgString.replace(/y="[\d.\-]+"/, `y="${finalY}"`);
            svgString = svgString.replace(/width="[\d.\-]+"/, `width="${finalWidth}"`);
            svgString = svgString.replace(/height="[\d.\-]+"/, `height="${finalHeight}"`);
          }
        } else if (obj.type === 'rect') {
          const rectMatch = svgString.match(/x="([\d.\-]+)"\s+y="([\d.\-]+)".*?width="([\d.\-]+)"\s+height="([\d.\-]+)"/);
          if (rectMatch) {
            const x = parseFloat(rectMatch[1]);
            const y = parseFloat(rectMatch[2]);
            const width = parseFloat(rectMatch[3]);
            const height = parseFloat(rectMatch[4]);

            // Round to 2 decimal places to avoid floating point precision issues
            const finalX = Math.round((translateX + (x * scaleX)) * 100) / 100;
            const finalY = Math.round((translateY + (y * scaleY)) * 100) / 100;
            const finalWidth = Math.round((width * scaleX) * 100) / 100;
            const finalHeight = Math.round((height * scaleY) * 100) / 100;

            svgString = svgString.replace(/<g[^>]*>/, '');
            svgString = svgString.replace(/<\/g>/, '');
            // Remove CorelDRAW-incompatible attributes
            svgString = svgString.replace(/\s*rx="0"\s*ry="0"/, '');
            svgString = svgString.replace(/\s*vector-effect="non-scaling-stroke"/, '');
            svgString = svgString.replace(/x="[\d.\-]+"/, `x="${finalX}"`);
            svgString = svgString.replace(/y="[\d.\-]+"/, `y="${finalY}"`);
            svgString = svgString.replace(/width="[\d.\-]+"/, `width="${finalWidth}"`);
            svgString = svgString.replace(/height="[\d.\-]+"/, `height="${finalHeight}"`);
          }
        } else if (obj.type === 'circle') {
          const circleMatch = svgString.match(/r="([\d.\-]+)"/);
          if (circleMatch) {
            const r = parseFloat(circleMatch[1]);
            const finalCx = Math.round(translateX * 100) / 100;
            const finalCy = Math.round(translateY * 100) / 100;

            // If scales are different, convert to ellipse
            if (Math.abs(scaleX - scaleY) > 0.01) {
              const rx = Math.round((r * scaleX) * 100) / 100;
              const ry = Math.round((r * scaleY) * 100) / 100;

              svgString = svgString.replace(/<g[^>]*>/, '');
              svgString = svgString.replace(/<\/g>/, '');
              svgString = svgString.replace(/\s*vector-effect="non-scaling-stroke"/, '');
              // Convert circle to ellipse
              svgString = svgString.replace(/<circle/, '<ellipse');
              svgString = svgString.replace(/<\/circle>/, '</ellipse>');
              svgString = svgString.replace(/cx="[\d.\-]+"/, `cx="${finalCx}"`);
              svgString = svgString.replace(/cy="[\d.\-]+"/, `cy="${finalCy}"`);
              svgString = svgString.replace(/r="[\d.\-]+"/, `rx="${rx}" ry="${ry}"`);
            } else {
              // Uniform scale - keep as circle
              const finalR = Math.round((r * scaleX) * 100) / 100;

              svgString = svgString.replace(/<g[^>]*>/, '');
              svgString = svgString.replace(/<\/g>/, '');
              svgString = svgString.replace(/\s*vector-effect="non-scaling-stroke"/, '');
              svgString = svgString.replace(/cx="[\d.\-]+"/, `cx="${finalCx}"`);
              svgString = svgString.replace(/cy="[\d.\-]+"/, `cy="${finalCy}"`);
              svgString = svgString.replace(/r="[\d.\-]+"/, `r="${finalR}"`);
            }
          }
        } else if (obj.type === 'triangle' || obj.type === 'polygon') {
          // For polygons, we need to transform the path coordinates
          const pathMatch = svgString.match(/d="([^"]+)"/);
          if (pathMatch) {
            const pathData = pathMatch[1];
            // Apply transform matrix to path - parse and transform each coordinate
            const transformedPath = pathData.replace(/([-\d.]+)\s+([-\d.]+)/g, (match, x, y) => {
              const numX = parseFloat(x);
              const numY = parseFloat(y);
              const newX = Math.round((translateX + (numX * scaleX)) * 100) / 100;
              const newY = Math.round((translateY + (numY * scaleY)) * 100) / 100;
              return `${newX} ${newY}`;
            });

            svgString = svgString.replace(/<g[^>]*>/, '');
            svgString = svgString.replace(/<\/g>/, '');
            svgString = svgString.replace(/\s*vector-effect="non-scaling-stroke"/, '');
            svgString = svgString.replace(/d="[^"]+"/, `d="${transformedPath}"`);
          }
        }
      }
    }

    // Images go outside the scale group (already scaled by 1.92 in flattening)
    if (obj.type === 'image') {
      imagesSvg += svgString;
    } else {
      objectsSvg += svgString;
    }
  }

  // Add background if requested
  let backgroundRect = '';
  if (includeSheet) {
    const bgColor = canvas.backgroundColor || '#ffffff';
    backgroundRect = `<rect x="0" y="0" width="100%" height="100%" fill="${bgColor}"></rect>`;
  }

  // CorelDRAW interprets SVG coordinates as 96 DPI by default
  // We need to scale from our 50 PPI to 96 DPI: 96/50 = 1.92
  const scaleFactor = 96 / PPI; // 96 / 50 = 1.92
  const viewBoxWidth = backingW * scaleFactor;
  const viewBoxHeight = backingH * scaleFactor;

  let svg = `<?xml version="1.0" encoding="UTF-8" standalone="no" ?>
<!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="${lengthInches}in" height="${heightInches}in" viewBox="0 0 ${viewBoxWidth} ${viewBoxHeight}" xml:space="preserve">
<desc>Created with Fabric.js 5.1.0</desc>
<defs>
</defs>
${backgroundRect}
<g transform="scale(${scaleFactor})">
${objectsSvg}
</g>
${imagesSvg}
</svg>`;

  // Fix color codes - Fabric sometimes exports @ instead of # in hex colors
  svg = svg.replace(/fill="@([0-9a-fA-F]{6})"/g, 'fill="#$1"');
  svg = svg.replace(/stroke="@([0-9a-fA-F]{6})"/g, 'stroke="#$1"');
  svg = svg.replace(/stop-color="@([0-9a-fA-F]{6})"/g, 'stop-color="#$1"');

  canvas.backgroundColor = originalBg;
  canvas.renderAll();
  return svg;
}

// Note: the "Save as SVG" button has been removed from the UI — the cut
// file is now captured automatically (via buildDesignSvg(), still used by
// embed-bridge.js) and attached to the WooCommerce order directly, so
// there's no separate manual export step for the customer.

// ---------- Measurements ----------
function updateMeasurements() {
  // "Include the full vinyl sheet" already changes what's actually priced
  // and cut (see computeSqft() in embed-bridge.js) — the lettering's own
  // size makes no difference once you're ordering the whole sheet, so the
  // Design Measurements panel should show the SHEET size in that case, not
  // the lettering's ink footprint. Otherwise the on-screen number wouldn't
  // match what's actually being priced/produced.
  const includeSheet = els.inputIncludeSheet ? els.inputIncludeSheet.checked : false;

  const measureNoteEl = document.getElementById("measureNote");

  if (includeSheet) {
    const sheetWidthIn = backingW / PPI;
    const sheetHeightIn = backingH / PPI;
    els.measWidth.textContent = sheetWidthIn.toFixed(2) + '"';
    els.measHeight.textContent = sheetHeightIn.toFixed(2) + '"';
    if (measureNoteEl) measureNoteEl.textContent = "Full vinyl sheet size — this is what's priced and cut, regardless of what's on it.";
    document.dispatchEvent(new CustomEvent("vinyl:measurements-changed"));
    return;
  }

  const bounds = getWholeDesignInkBoundsPx();
  if (!bounds) {
    els.measWidth.textContent = '0.00"';
    els.measHeight.textContent = '0.00"';
    if (measureNoteEl) measureNoteEl.textContent = "Total bounding box of all lettering on the sheet, in real cut inches.";
    document.dispatchEvent(new CustomEvent("vinyl:measurements-changed"));
    return;
  }
  const widthIn = (bounds.maxX - bounds.minX) / PPI;
  const heightIn = (bounds.maxY - bounds.minY) / PPI;
  els.measWidth.textContent = widthIn.toFixed(2) + '"';
  els.measHeight.textContent = heightIn.toFixed(2) + '"';
  if (measureNoteEl) measureNoteEl.textContent = "Total bounding box of all lettering on the sheet, in real cut inches.";
  // Fired on every measurement recompute — the WooCommerce embed bridge
  // listens for this to re-send the live price, regardless of which control
  // (typing, font pick, slider, stroke toggle, drag, undo…) triggered it.
  document.dispatchEvent(new CustomEvent("vinyl:measurements-changed"));
}
const updateMeasurementsDebounced = debounce(updateMeasurements, 120);

// Get actual ink bounds (visible pixels) of an object in canvas coordinates
function getObjectInkBounds(obj) {
  try {
    obj.dirty = true;
    const multiplier = 2; // lower multiplier for performance during bounds check
    const el = obj.toCanvasElement({ multiplier, enableRetinaScaling: false });
    if (!el.width || !el.height) return null;

    const ctx = el.getContext("2d");
    const inkBounds = scanAlphaBounds(ctx, el.width, el.height);
    if (!inkBounds) return null;

    // Convert from object's local coordinates to canvas coordinates
    const bound = obj.getBoundingRect();
    const scaleX = bound.width / el.width;
    const scaleY = bound.height / el.height;

    return {
      left: bound.left + (inkBounds.minX * scaleX),
      top: bound.top + (inkBounds.minY * scaleY),
      width: (inkBounds.maxX - inkBounds.minX + 1) * scaleX,
      height: (inkBounds.maxY - inkBounds.minY + 1) * scaleY
    };
  } catch (err) {
    // Fallback to regular bounds if ink detection fails
    return obj.getBoundingRect();
  }
}

// Check if any objects are outside the sheet boundaries and update hint text
function checkBoundsWarning() {
  if (!els.canvasHint) return;

  const objects = canvas.getObjects();
  let hasOutOfBounds = false;

  for (const obj of objects) {
    // Get actual ink bounds (visible pixels only)
    const bound = getObjectInkBounds(obj);
    if (!bound) continue;

    // Check if any part of the actual ink is outside the canvas (sheet) boundaries
    if (bound.left < 0 || bound.top < 0 || 
        bound.left + bound.width > canvas.width || 
        bound.top + bound.height > canvas.height) {
      hasOutOfBounds = true;
      break;
    }
  }

  if (hasOutOfBounds) {
    els.canvasHint.textContent = '⚠️ Some text is outside the sheet boundaries! Click "Center All" to get it back.';
    els.canvasHint.style.color = "#f59e0b";
    els.canvasHint.style.fontWeight = "600";
  } else {
    els.canvasHint.textContent = "Drag to move · corner handles to resize (locked proportional) · edit text in the sidebar box";
    els.canvasHint.style.color = "";
    els.canvasHint.style.fontWeight = "";
  }
}
const checkBoundsWarningDebounced = debounce(checkBoundsWarning, 100);

function updateSelectedMeasurements(obj) {
  if (!obj) return clearSelectedMeasurements();
  const ink = getObjectInkSizePx(obj);
  els.selWidth.textContent = (ink.width / PPI).toFixed(2) + '"';
  els.selHeight.textContent = (ink.height / PPI).toFixed(2) + '"';
  if (obj.type === "text") {
    const impliedHeight = ink.height / PPI;
    if (impliedHeight > 0) {
      els.inputHeight.value = Math.min(22, Math.max(1, impliedHeight)).toFixed(2);
      els.heightVal.textContent = parseFloat(els.inputHeight.value).toFixed(2) + '"';
    }
    els.selectFont.value = obj.fontFamily;
    els.inputItalic.checked = obj.fontStyle === "italic";
    // obj.fill is either a plain hex string (solid) or a fabric.Gradient
    // instance (gradient) — reflect whichever is actually applied instead
    // of assuming a string, which would throw on a Gradient object.
    const isGradient = obj.fill && typeof obj.fill === "object";
    els.inputGradientEnabled.checked = isGradient;
    setGradientControlsEnabled(isGradient);
    if (isGradient) {
      const stops = obj.fill.colorStops || [];
      if (stops[0]) els.inputGradientColor1.value = rgbToHex(stops[0].color);
      if (stops.length) els.inputGradientColor2.value = rgbToHex(stops[stops.length - 1].color);
      els.selectGradientDirection.value = directionFromGradientCoords(obj.fill.coords);
    } else {
      els.inputColor.value = rgbToHex(obj.fill);
    }
    els.inputSpacing.value = obj.charSpacing || 0;
    const hasStroke = !!obj.stroke && (obj.strokeWidth || 0) > 0;
    // Stroke controls removed - using unified bottom palette instead
    // els.inputStrokeEnabled.checked = hasStroke;
    // setStrokeControlsEnabled(hasStroke);
    if (hasStroke) {
      // els.inputStrokeColor.value = rgbToHex(obj.stroke);
      // els.inputStrokeWidth.value = obj.strokeWidth;
      if (els.selectStrokePosition) {
        els.selectStrokePosition.value = obj.paintFirst || 'fill';
      }
    }
    if (document.activeElement !== els.inputText) {
      els.inputText.value = obj.text;
    }
    refreshAllPaletteSwatches();
  } else {
    // For clipart/shapes (non-text objects)
    const clipartFillInput = document.getElementById('inputClipartFill');
    const clipartStrokeCheckbox = document.getElementById('inputClipartStroke');
    const clipartStrokeColorInput = document.getElementById('inputClipartStrokeColor');
    const clipartStrokeWidthInput = document.getElementById('inputClipartStrokeWidth');

    // Get fill from first object if it's a group
    let fillColor = obj.fill;
    let strokeColor = obj.stroke;
    let strokeWidth = obj.strokeWidth || 2;
    let hasStroke = !!strokeColor && (strokeWidth || 0) > 0;

    if (obj.type === 'group' && obj._objects && obj._objects.length > 0) {
      const firstChild = obj._objects[0];
      fillColor = firstChild.fill;
      strokeColor = firstChild.stroke;
      strokeWidth = firstChild.strokeWidth || 2;
      hasStroke = !!strokeColor && (strokeWidth || 0) > 0;
    }

    if (clipartFillInput) {
      clipartFillInput.value = rgbToHex(fillColor);
    }

    if (clipartStrokeCheckbox) {
      clipartStrokeCheckbox.checked = hasStroke;
      const clipartStrokeControls = document.getElementById('clipartStrokeControls');
      if (clipartStrokeControls) {
        clipartStrokeControls.style.display = hasStroke ? 'block' : 'none';
      }
    }

    if (hasStroke) {
      if (clipartStrokeColorInput) clipartStrokeColorInput.value = rgbToHex(strokeColor);
      if (clipartStrokeWidthInput) clipartStrokeWidthInput.value = strokeWidth;
    }

    refreshAllPaletteSwatches();
  }
}

function clearSelectedMeasurements() {
  els.selWidth.textContent = "–";
  els.selHeight.textContent = "–";
}

function rgbToHex(color) {
  if (!color) return "#111111";
  // Handle gradient objects
  if (typeof color === 'object') {
    // If it's a gradient, try to get the first color stop
    if (color.colorStops && color.colorStops.length > 0) {
      return color.colorStops[0].color || "#111111";
    }
    return "#111111";
  }
  // Handle string colors
  if (typeof color === 'string' && color.startsWith("#")) return color;
  return "#111111";
}

canvas.on("selection:created", () => {
  updateSelectedMeasurements(canvas.getActiveObject());
  setLayerButtonsEnabled(true);
  checkBoundsWarningDebounced();
});
canvas.on("selection:updated", () => {
  updateSelectedMeasurements(canvas.getActiveObject());
  setLayerButtonsEnabled(true);
  checkBoundsWarningDebounced();
});
canvas.on("selection:cleared", () => {
  clearSelectedMeasurements();
  setLayerButtonsEnabled(false);
  checkBoundsWarningDebounced();
});
canvas.on("object:modified", () => {
  updateMeasurementsDebounced();
  updateSelectedMeasurements(canvas.getActiveObject());
  updateLivePreview();
  checkBoundsWarningDebounced();
  saveState();
});
canvas.on("object:scaling", () => {
  // Cheap per-object update only — whole-design ink scan is deferred to
  // object:modified (fires once, at the end of the interaction).
  updateSelectedMeasurements(canvas.getActiveObject());
  checkBoundsWarningDebounced();
});
canvas.on("object:moving", () => {
  updateMeasurementsDebounced();
  checkBoundsWarningDebounced();
});
canvas.on("object:added", () => {
  checkBoundsWarningDebounced();
});
canvas.on("object:removed", () => {
  checkBoundsWarningDebounced();
});

// ---------- Live thumbnail preview ----------
function updateLivePreview() {
  // Disabled - livePreview element was removed from UI
  return;
}

// ---------- Init ----------
els.heightVal.textContent = parseFloat(els.inputHeight.value).toFixed(2) + '"';
// setStrokeControlsEnabled(false); // Stroke controls removed
setGradientControlsEnabled(false);
setLayerButtonsEnabled(false);
zoomToFit();
updateMeasurements();
updateLivePreview();
updateUndoRedoButtons();

// Initialize clipart library
initializeClipart();

// Clipart stroke toggle
const clipartStrokeCheckbox = document.getElementById('inputClipartStroke');
const clipartStrokeControls = document.getElementById('clipartStrokeControls');
if (clipartStrokeCheckbox && clipartStrokeControls) {
  clipartStrokeCheckbox.addEventListener('change', () => {
    clipartStrokeControls.style.display = clipartStrokeCheckbox.checked ? 'block' : 'none';

    // Apply to selected clipart/shape
    const obj = canvas.getActiveObject();
    if (obj && obj.type !== 'text') {
      if (clipartStrokeCheckbox.checked) {
        const strokeColor = document.getElementById('inputClipartStrokeColor')?.value || '#000000';
        const strokeWidth = parseInt(document.getElementById('inputClipartStrokeWidth')?.value, 10) || 2;

        // Apply to grouped objects or single object
        if (obj.type === 'group') {
          obj.forEachObject((child) => {
            child.set({
              stroke: strokeColor,
              strokeWidth: strokeWidth,
              strokeUniform: true
            });
          });
        } else {
          obj.set({
            stroke: strokeColor,
            strokeWidth: strokeWidth,
            strokeUniform: true
          });
        }
      } else {
        // Remove stroke
        if (obj.type === 'group') {
          obj.forEachObject((child) => {
            child.set({ stroke: null, strokeWidth: 0 });
          });
        } else {
          obj.set({ stroke: null, strokeWidth: 0 });
        }
      }
      obj.setCoords();
      canvas.renderAll();
      saveState();
    }
  });
}

// Clipart fill color change
const clipartFillInput = document.getElementById('inputClipartFill');
if (clipartFillInput) {
  clipartFillInput.addEventListener('input', () => {
    const obj = canvas.getActiveObject();
    if (obj && obj.type !== 'text') {
      // Apply to grouped objects or single object
      if (obj.type === 'group') {
        obj.forEachObject((child) => {
          child.set('fill', clipartFillInput.value);
        });
      } else {
        obj.set('fill', clipartFillInput.value);
      }
      canvas.renderAll();
      saveState();
    }
  });
}

// Clipart stroke color change
const clipartStrokeColorInput = document.getElementById('inputClipartStrokeColor');
if (clipartStrokeColorInput) {
  clipartStrokeColorInput.addEventListener('input', () => {
    const obj = canvas.getActiveObject();
    if (obj && obj.type !== 'text') {
      if (obj.type === 'group') {
        obj.forEachObject((child) => {
          if (child.stroke) child.set('stroke', clipartStrokeColorInput.value);
        });
      } else if (obj.stroke) {
        obj.set('stroke', clipartStrokeColorInput.value);
      }
      canvas.renderAll();
      saveState();
    }
  });
}

// Clipart stroke width change
const clipartStrokeWidthInput = document.getElementById('inputClipartStrokeWidth');
if (clipartStrokeWidthInput) {
  clipartStrokeWidthInput.addEventListener('input', () => {
    const obj = canvas.getActiveObject();
    if (obj && obj.type !== 'text') {
      const width = parseInt(clipartStrokeWidthInput.value, 10);
      if (obj.type === 'group') {
        obj.forEachObject((child) => {
          if (child.stroke) child.set('strokeWidth', width);
        });
      } else if (obj.stroke) {
        obj.set('strokeWidth', width);
      }
      canvas.renderAll();
      saveState();
    }
  });
}

// ==================================================================
// Unified Color Palette Bar
// ==================================================================
const unifiedStrokeToggle = document.getElementById('unifiedStrokeToggle');
const unifiedStrokeWidthInput = document.getElementById('unifiedStrokeWidth');

// Fill color change from unified palette
unifiedFillInput.addEventListener('input', () => {
  const obj = canvas.getActiveObject();
  if (!obj) return;

  if (obj.type === 'text' || obj.type === 'i-text') {
    obj.set('fill', unifiedFillInput.value);
  } else if (obj.type === 'group') {
    obj.forEachObject((child) => {
      child.set('fill', unifiedFillInput.value);
    });
  } else {
    obj.set('fill', unifiedFillInput.value);
  }

  canvas.renderAll();
  saveState();
});

// Stroke toggle from unified palette
if (unifiedStrokeToggle) {
  unifiedStrokeToggle.addEventListener('change', () => {
    const obj = canvas.getActiveObject();
    if (!obj) return;

    const enabled = unifiedStrokeToggle.checked;
    const color = unifiedStrokeInput.value;
    const width = parseInt(unifiedStrokeWidthInput.value, 10);

    if (obj.type === 'text' || obj.type === 'i-text') {
      if (enabled) {
        obj.set({ stroke: color, strokeWidth: width });
      } else {
        obj.set({ stroke: null, strokeWidth: 0 });
      }
    } else if (obj.type === 'group') {
      obj.forEachObject((child) => {
        if (enabled) {
          child.set({ stroke: color, strokeWidth: width, strokeUniform: true });
        } else {
          child.set({ stroke: null, strokeWidth: 0 });
        }
      });
    } else {
      if (enabled) {
        obj.set({ stroke: color, strokeWidth: width, strokeUniform: true });
      } else {
        obj.set({ stroke: null, strokeWidth: 0 });
      }
    }

    canvas.renderAll();
    saveState();
  });
}

// Stroke color change from unified palette
unifiedStrokeInput.addEventListener('input', () => {
  const obj = canvas.getActiveObject();
  if (!obj || !unifiedStrokeToggle.checked) return;

  if (obj.type === 'text' || obj.type === 'i-text') {
    obj.set('stroke', unifiedStrokeInput.value);
  } else if (obj.type === 'group') {
    obj.forEachObject((child) => {
      if (child.stroke) child.set('stroke', unifiedStrokeInput.value);
    });
  } else if (obj.stroke) {
    obj.set('stroke', unifiedStrokeInput.value);
  }

  canvas.renderAll();
  saveState();
});

// Stroke width change from unified palette
if (unifiedStrokeWidthInput) {
  unifiedStrokeWidthInput.addEventListener('input', () => {
    const obj = canvas.getActiveObject();
    if (!obj || !unifiedStrokeToggle.checked) return;

    const width = parseInt(unifiedStrokeWidthInput.value, 10);

    if (obj.type === 'text' || obj.type === 'i-text') {
      obj.set('strokeWidth', width);
    } else if (obj.type === 'group') {
      obj.forEachObject((child) => {
        if (child.stroke) child.set('strokeWidth', width);
      });
    } else if (obj.stroke) {
      obj.set('strokeWidth', width);
    }

    canvas.renderAll();
    saveState();
  });
}

// Update unified palette when object is selected
canvas.on('selection:created', updateUnifiedPalette);
canvas.on('selection:updated', updateUnifiedPalette);

function updateUnifiedPalette() {
  const obj = canvas.getActiveObject();
  if (!obj) return;

  // Update fill color
  if (obj.type === 'text' || obj.type === 'i-text') {
    unifiedFillInput.value = obj.fill || '#000000';
  } else if (obj.type === 'group') {
    // Get fill from first child
    const firstChild = obj._objects[0];
    if (firstChild) {
      unifiedFillInput.value = firstChild.fill || '#000000';
    }
  } else {
    unifiedFillInput.value = obj.fill || '#000000';
  }

  // Update stroke
  let hasStroke = false;
  let strokeColor = '#000000';
  let strokeWidth = 2;

  if (obj.type === 'text' || obj.type === 'i-text') {
    hasStroke = !!obj.stroke;
    strokeColor = obj.stroke || '#000000';
    strokeWidth = obj.strokeWidth || 2;
  } else if (obj.type === 'group') {
    const firstChild = obj._objects[0];
    if (firstChild && firstChild.stroke) {
      hasStroke = true;
      strokeColor = firstChild.stroke;
      strokeWidth = firstChild.strokeWidth || 2;
    }
  } else {
    hasStroke = !!obj.stroke;
    strokeColor = obj.stroke || '#000000';
    strokeWidth = obj.strokeWidth || 2;
  }

  unifiedStrokeToggle.checked = hasStroke;
  unifiedStrokeInput.value = strokeColor;
  unifiedStrokeWidthInput.value = strokeWidth;

  // Refresh palette swatches to show active colors
  refreshAllPaletteSwatches();
}

// ==================================================================
// Export Functions (SVG & PNG)
// ==================================================================

/**
 * Generates a PNG thumbnail of the current design
 * @param {number} multiplier - Scale factor (0.3 = 30% size, prevents huge base64 strings)
 * @returns {string} Base64-encoded PNG data URL
 */
function buildPreviewPng(multiplier = 0.3) {
  return canvas.toDataURL({ format: "png", multiplier });
}

/**
 * Downloads SVG file to user's computer
 * Note: If objects appear misaligned in CorelDRAW/Illustrator, use "Ungroup" to flatten transforms
 */
function downloadSvg() {
  const includeSheet = els.inputIncludeSheet?.checked || false;
  const svg = buildDesignSvg(includeSheet);
  const blob = new Blob([svg], { type: "image/svg+xml" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `vinyl-design-${Date.now()}.svg`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  // Show helpful tip for CorelDRAW users
  console.log("SVG exported! If objects appear rotated in CorelDRAW, select all and use 'Ungroup' to flatten transforms.");
}

/**
 * Downloads PNG file to user's computer
 */
function downloadPng() {
  // PNG files are interpreted at 72 DPI by default
  // Our canvas is 50 PPI, so we need to scale by 72/50 = 1.44
  // This ensures the PNG appears at the correct physical size (e.g., 12" × 24")
  const link = document.createElement("a");
  link.href = canvas.toDataURL({ format: "png", multiplier: 1.44 });
  link.download = `vinyl-design-${Date.now()}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Prepares design data package for WooCommerce integration
 * @returns {object} Complete design data including SVG, PNG, measurements, pricing
 */
function prepareDesignData() {
  const dims = getCurrentMeasurements();
  const includeSheet = els.inputIncludeSheet?.checked || false;

  // Get all canvas objects as serializable data
  const objects = canvas.getObjects().map((obj) => ({
    type: obj.type,
    text: obj.text || null,
    fontFamily: obj.fontFamily || null,
    fontSize: obj.fontSize || null,
    fill: obj.fill || null,
    stroke: obj.stroke || null,
    left: obj.left,
    top: obj.top,
    width: obj.width,
    height: obj.height,
    scaleX: obj.scaleX,
    scaleY: obj.scaleY,
    angle: obj.angle || 0,
  }));

  return {
    version: "2.2.3",
    timestamp: new Date().toISOString(),
    objects,
    sheet: {
      widthIn: Math.round((backingW / PPI) * 100) / 100,
      heightIn: Math.round((backingH / PPI) * 100) / 100,
      color: canvas.backgroundColor || "#ffffff",
    },
    design: {
      widthIn: dims.widthIn,
      heightIn: dims.heightIn,
      sqft: dims.sqft,
      includesSheet: includeSheet,
    },
    // Small PNG thumbnail for cart/order preview (0.3x to avoid huge POST data)
    previewPng: buildPreviewPng(0.3),
    // Full SVG for production/cutting
    designSvg: buildDesignSvg(includeSheet),
  };
}

/**
 * Sends design data to parent window (for WordPress/WooCommerce iframe embed)
 */
function sendDesignToWooCommerce() {
  const data = prepareDesignData();

  // Check if we're in an iframe (WooCommerce product page)
  if (window.parent && window.parent !== window) {
    window.parent.postMessage({
      type: "vinyl_design_data",
      data: data
    }, "*");
    console.log("Design data sent to WooCommerce:", data);
  } else {
    // Standalone mode - just show the data package
    console.log("Design data prepared (standalone mode):", data);
    alert("Design data ready! Check console for details.\n\n" +
          `Design Size: ${data.design.widthIn}" × ${data.design.heightIn}"\n` +
          `Square Feet: ${data.design.sqft} sqft`);
  }
}

populateFontSelect(); // seed the dropdown with bundled defaults immediately
injectFontFaceStyles(); // and their real @font-face rules, immediately too

// Gate the whole app on fonts being fully decoded before any text is added
// or measured — this is the fix for fonts only ever measuring correctly
// "the first time" / only working for system TrueType fonts. First checks
// for an admin-managed font list (WooCommerce → Vinyl Designer → Fonts);
// falls back to the bundled defaults if not embedded or the fetch fails.
fetchManagedFontList()
  .then((managed) => {
    if (managed && managed.length) {
      FONT_FILES = managed;
      rebuildFontWeights();
      populateFontSelect();
      injectFontFaceStyles();
    }
    return preloadFonts();
  })
  .then(() => {
    els.fontLoadStatus.textContent = "Fonts ready";
    els.fontLoadStatus.classList.add("ready");
    setUiEnabled(true);
    canvas.renderAll();
    vldFontsReady = true;
    if (vldEditDesignPayload) {
      // A saved cart-item design arrived (possibly before fonts finished
      // loading) — restore it instead of seeding the default example text.
      applyEditDesignPayload(vldEditDesignPayload);
      vldEditDesignApplied = true;
    } else {
      // Seed with one example text so the canvas isn't empty.
      addText();
    }
    setTimeout(() => {
      els.fontLoadStatus.style.display = "none";
    }, 1500);
  });

// Export globals for checkout integration
window.canvas = canvas;
window.PPI = PPI;
window.backingW = backingW;
window.backingH = backingH;
window.buildDesignSvg = buildDesignSvg;
